import { apiFetch } from '@/lib/api';

// ===============================
// Type Definitions
// ===============================

export interface LocationJson {
  formatted_address: string;
  place_id: string;
  geometry?: {
    location: { lat: number; lng: number };
  };
  address_components?: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
}

/**
 * Search mode for destination venue discovery
 * - 'city_name': Match venues by city/country name fields (default)
 * - 'radius': Search venues within geo-radius of destination lat/lon
 */
export type DestinationSearchMode = 'city_name' | 'radius';
export type DestinationType = 'city' | 'region' | 'country';
export type RegionSource = 'google_places' | 'custom' | 'polygon';

export interface ResearchDestination {
  id: string;
  event_id: string;
  organization_id: string;
  city: string;
  country: string;
  region: string | null;
  place_id: string | null;
  location_json: LocationJson | null;
  // Search settings
  search_mode: DestinationSearchMode;
  search_radius_km: number | null;
  sort_order: number;
  status: 'active' | 'archived';
  // Destination grouping
  parent_destination_id: string | null;
  destination_type: DestinationType;
  display_name: string | null;
  region_source: RegionSource | null;
  activities_initialized?: boolean;
  created_at: string;
  updated_at: string;
}

export interface DestinationWithCount extends ResearchDestination {
  shortlist_count: number;
  accepted_count?: number;
  total_in_database?: number;
  total_unfiltered?: number;
  total_filtered?: number;
  children?: DestinationWithCount[];
}

export interface DiscoveredCity {
  city: string;
  country: string;
  relevance: string;
  has_hotels: boolean;
}

export interface CreateRegionInput {
  display_name: string;
  country: string;
  region_source: RegionSource;
  place_id?: string;
  location_json?: Record<string, any>;
  cities: Array<{
    city: string;
    country: string;
    place_id?: string;
    location_json?: any;
  }>;
}

export interface CreateDestinationInput {
  city: string;
  country: string;
  region?: string;
  place_id?: string;
  location_json?: LocationJson;
  sort_order?: number;
  destination_type?: 'city' | 'region' | 'country';
  display_name?: string;
  // Search settings
  search_mode?: DestinationSearchMode; // Defaults to 'city_name'
  search_radius_km?: number; // Only used when search_mode is 'radius' (1-50km)
}

export interface VenueShortlistItem {
  id: string;
  research_destination_id: string;
  venue_id: string;
  added_at: string;
  added_by_user_id: string;
  sort_order: number;
  match_score: number | null;
  match_reasoning: string | null;
  status: 'shortlisted' | 'rfp_sent' | 'rfp_received' | 'selected' | 'rejected';
  // One hotel note shared by Research, RFPs and Selection
  notes: string | null;
  // false = Internal (default); true = shown on client share links
  notes_client_facing?: boolean;
  // Cvent RFP Integration
  cvent_supplier_id: string | null;
  cvent_match_confidence: number | null;
  cvent_match_signals: Record<string, any> | null;
  cvent_connected_at: string | null;
  // Cvent Proposal Data
  cvent_proposal_status: string | null;
  cvent_lowest_room_rate: number | null;
  cvent_total_cost: number | null;
  cvent_currency_code: string | null;
  cvent_original_response_date: string | null;
  cvent_last_response_date: string | null;
  cvent_proposal_data_refreshed_at: string | null;
  cvent_proposal_pdf_attachment_id: string | null;
  cvent_proposal_pdf_fetched_at: string | null;
  // Full Cvent Proposal Data
  cvent_proposal_data_jsonb: Record<string, any> | null;
  cvent_max_room_rate: number | null;
  cvent_total_sleeping_room_cost: number | null;
  cvent_total_fnb_cost: number | null;
  cvent_total_misc_cost: number | null;
  cvent_applicable_tax_percent: number | null;
  cvent_service_charge_percent: number | null;
  cvent_commission_rate: number | null;
  cvent_food_beverage_minimum: number | null;
  cvent_is_tax_inclusive: boolean | null;
  cvent_is_breakfast_inclusive: boolean | null;
  cvent_proposal_full_data_stored_at: string | null;
  cvent_proposal_summary: string | null;
  // Manual cost overrides
  override_lowest_room_rate: number | null;
  override_total_cost: number | null;
  // Selection step approval
  approval_status: 'approved' | 'denied' | null;
  decision_note: string | null;
  comparison_budget_note: string | null;
  decision_updated_at: string | null;
  decision_updated_by_user_id: string | null;
  // Team recommendation
  is_recommended: boolean;
  // Buyout marker
  is_buyout: boolean;
  // All-inclusive marker
  is_all_inclusive: boolean;
  // Hidden from client-facing proposal
  is_hidden: boolean;
  // Manual proposal flag
  is_manual_proposal: boolean | null;
  // Manual proposal dates
  proposal_check_in_date: string | null;
  proposal_check_out_date: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Complete venue data structure
 * Matches VenueWithRanking from backend (server/modules/research/research.types.ts)
 * This ensures all venue data is consistently typed across the application
 */
export interface VenueDetails {
  // Core venue identification
  id: string;
  organization_id: string;
  property_name: string;
  
  // Location data
  region: string | null;
  country: string | null;
  state: string | null;
  city: string;
  address: string | null;
  website: string | null;
  
  // Property details
  number_of_rooms: number | null;
  meeting_spaces: number | null;
  maximum_capacity: number | null;
  star_rating: number | null;
  
  // Amenities
  number_of_restaurants: number | null;
  number_of_bars: number | null;
  number_of_pools: number | null;
  spa_facilities: boolean | null;
  beach_access: 'beachfront' | 'beach_access' | 'none' | null;
  all_inclusive_available: boolean | null;
  property_type: 'adults_only' | 'family_friendly' | 'mixed' | null;
  golf_course: 'onsite' | 'nearby' | 'none' | null;
  
  // TripAdvisor
  tripadvisor_link: string | null;
  tripadvisor_rating: number | null;
  tripadvisor_review_count: number | null;
  tripadvisor_reviews_json: Array<{
    title: string;
    text: string;
    rating: number;
    date: string;
    author: string;
  }> | null;
  
  // Geolocation
  lat: number | null;
  lon: number | null;
  place_id: string | null;
  
  // Airport proximity
  nearest_airport_iata: string | null;
  nearest_airport_distance_km: number | null;
  nearest_airport_name: string | null;
  travel_time_minutes: number | null;
  
  // Enrichment metadata
  enrichment_status: string;
  enrichment_attempted_at: Date | null;
  enrichment_completed_at: Date | null;
  enrichment_error: string | null;
  enrichment_source: string | null;
  
  // Active enrichment job info (from jobs table join)
  enrichment_job_status?: 'queued' | 'processing' | null;
  enrichment_job_started_at?: Date | null;
  
  // AI ranking metadata (from research/search context)
  match_score: number | null;
  match_reasoning: string | null;
  recommendation: string | null;
}

export interface ShortlistWithVenue extends VenueShortlistItem {
  venue: VenueDetails;
}

export interface AddToShortlistInput {
  venue_id: string;
  match_score?: number;
  match_reasoning?: string;
  notes?: string;
  sort_order?: number;
}

export interface UpdateShortlistInput {
  notes?: string;
  notes_client_facing?: boolean;
  status?: 'shortlisted' | 'rfp_sent' | 'rfp_received' | 'selected' | 'rejected';
  sort_order?: number;
  // Cvent proposal data
  cvent_proposal_status?: string | null;
  cvent_lowest_room_rate?: number | null;
  cvent_total_cost?: number | null;
  cvent_currency_code?: string | null;
  cvent_original_response_date?: Date | string | null;
  cvent_last_response_date?: Date | string | null;
  cvent_proposal_data_refreshed_at?: Date | string | null;
  // Manual cost overrides
  override_lowest_room_rate?: number | null;
  override_total_cost?: number | null;
  // Selection step approval
  approval_status?: 'approved' | 'denied' | null;
  decision_note?: string | null;
  comparison_budget_note?: string | null;
  // Team recommendation
  is_recommended?: boolean;
  // Buyout marker
  is_buyout?: boolean;
  // All-inclusive marker
  is_all_inclusive?: boolean;
  // Hidden from client-facing proposal
  is_hidden?: boolean;
}

export interface ProposalActivitySource { id:string; city:string; active_count:number; total_count:number; activities_initialized:boolean; }

export interface SubmittedProposalsResponse {
  grouped_by_city: Array<{
    proposal_destination_ids: string[];
    content_destination_id: string | null;
    activity_sources: ProposalActivitySource[];
    city: string;
    country: string;
    destinations: Array<{
      destination: ResearchDestination;
      shortlist: ShortlistWithVenue[];
    }>;
  }>;
  destinations: Array<{
    destination: ResearchDestination;
    shortlist: ShortlistWithVenue[];
  }>;
  totals: {
    submitted: number;
    pending: number;
    approved: number;
    denied: number;
  };
}

export interface SearchCriteria {
  city: string;
  country: string | null;
  min_rooms: number | null;
  max_rooms: number | null;
  min_conference_capacity: number | null;
  min_star_rating: number | null;
  max_star_rating: number | null;
  max_airport_distance_km: number | null;
  location_preferences: string[];
  budget_tier: string | null;
  
  // Amenities
  has_spa?: boolean | null;
  min_restaurants?: number | null;
  min_bars?: number | null;
  min_pools?: number | null;
  beach_access?: 'beachfront' | 'beach_access' | 'none' | null;
  all_inclusive?: boolean | null;
  property_type?: 'adults_only' | 'family_friendly' | 'mixed' | null;
  has_golf?: boolean | null;
  min_tripadvisor_rating?: number | null;
}

export interface SearchContext {
  client_name: string | null;
  event_name: string | null;
  event_notes: string | null;
  attendee_count: number | null;
  destination: string;
}

export interface ExplicitSearchFilters {
  min_rooms?: number | null;
  min_conference_capacity?: number | null;
  star_rating?: number | null;
  max_airport_distance_km?: number | null;
  has_spa?: boolean | null;
  min_restaurants?: number | null;
  min_bars?: number | null;
  min_pools?: number | null;
  beach_access?: 'beachfront' | 'beach_access' | 'none' | null;
  all_inclusive?: boolean | null;
  property_type?: 'adults_only' | 'family_friendly' | 'mixed' | null;
  has_golf?: boolean | null;
  min_tripadvisor_rating?: number | null;
}

export interface SearchResult {
  criteria: SearchCriteria;
  venues: VenueDetails[];
  total_count: number;
  natural_language_query?: string;
  performed_at?: string;
  performed_by_user_id?: string;
  explicit_filters?: ExplicitSearchFilters;
}

// ===============================
// Research Service
// ===============================

export class ResearchService {
  // ===============================
  // Event Notes
  // ===============================

  async updateEventNotes(eventId: string, notes: string): Promise<void> {
    const res = await apiFetch(`/api/events/${eventId}/notes`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_notes: notes }),
    });
    if (!res.ok) throw new Error('Failed to update event notes');
  }

  // ===============================
  // Destinations
  // ===============================

  async createDestination(
    eventId: string,
    input: CreateDestinationInput
  ): Promise<ResearchDestination> {
    const res = await apiFetch(`/api/events/${eventId}/destinations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error('Failed to create destination');
    const data = await res.json();
    return data.data.destination;
  }

  async listDestinations(eventId: string): Promise<DestinationWithCount[]> {
    const res = await apiFetch(`/api/events/${eventId}/destinations`);
    if (!res.ok) throw new Error('Failed to fetch destinations');
    const data = await res.json();
    return data.data.items;
  }

  async updateDestination(
    destinationId: string,
    input: {
      sort_order?: number;
      status?: 'active' | 'archived';
      search_mode?: DestinationSearchMode;
      search_radius_km?: number | null;
      location_json?: any;
      display_name?: string | null;
      parent_destination_id?: string | null;
    }
  ): Promise<ResearchDestination> {
    const res = await apiFetch(`/api/destinations/${destinationId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error('Failed to update destination');
    const data = await res.json();
    return data.data.destination;
  }

  async preflightDeleteDestination(destinationId: string): Promise<{
    canDelete: boolean;
    shortlistCount: number;
    cventConnectedCount: number;
    approvedCount: number;
    isRegion: boolean;
    childCount: number;
    blockReasons: string[];
  }> {
    const res = await apiFetch(`/api/destinations/${destinationId}/preflight-delete`);
    if (!res.ok) throw new Error('Failed to check destination');
    const json = await res.json();
    return json.data;
  }

  async deleteDestination(destinationId: string): Promise<void> {
    const res = await apiFetch(`/api/destinations/${destinationId}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const json = await res.json().catch(() => null);
      throw new Error(json?.message || 'Failed to delete destination');
    }
  }

  async reorderDestinations(eventId: string, destinationIds: string[]): Promise<void> {
    const res = await apiFetch(`/api/events/${eventId}/destinations/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ destination_ids: destinationIds }),
    });
    if (!res.ok) throw new Error('Failed to reorder destinations');
  }

  // ===============================
  // Regions (Destination Grouping)
  // ===============================

  async createRegion(
    eventId: string,
    input: CreateRegionInput
  ): Promise<{ region: ResearchDestination; children: ResearchDestination[] }> {
    const res = await apiFetch(`/api/events/${eventId}/regions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error('Failed to create region');
    const data = await res.json();
    return data.data;
  }

  async getRegionChildren(regionId: string): Promise<ResearchDestination[]> {
    const res = await apiFetch(`/api/regions/${regionId}/children`);
    if (!res.ok) throw new Error('Failed to fetch region children');
    const data = await res.json();
    return data.data.children;
  }

  async addCityToRegion(
    regionId: string,
    city: { city: string; country: string; place_id?: string; location_json?: any }
  ): Promise<ResearchDestination> {
    const res = await apiFetch(`/api/regions/${regionId}/cities`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(city),
    });
    if (!res.ok) throw new Error('Failed to add city to region');
    const data = await res.json();
    return data.data.city;
  }

  async removeCityFromRegion(cityId: string): Promise<void> {
    const res = await apiFetch(`/api/regions/cities/${cityId}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const json = await res.json().catch(() => null);
      throw new Error(json?.message || 'Failed to remove city from region');
    }
  }

  /**
   * Move a standalone city into a region, or extract a child city back to standalone.
   * Pass regionId = null to extract.
   */
  async moveDestinationToRegion(
    destinationId: string,
    regionId: string | null
  ): Promise<ResearchDestination> {
    return this.updateDestination(destinationId, {
      parent_destination_id: regionId,
    });
  }

  async discoverCities(
    regionName: string,
    country: string
  ): Promise<DiscoveredCity[]> {
    const res = await apiFetch('/api/regions/discover-cities', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ region_name: regionName, country }),
    });
    if (!res.ok) throw new Error('Failed to discover cities');
    const data = await res.json();
    return data.data.cities;
  }

  // ===============================
  // Venue Search
  // ===============================

  async searchVenues(
    destinationId: string,
    query: string,
    context: SearchContext,
    explicitFilters?: ExplicitSearchFilters
  ): Promise<SearchResult> {
    const res = await apiFetch(`/api/destinations/${destinationId}/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        natural_language_query: query,
        context,
        explicit_filters: explicitFilters,
      }),
    });
    if (!res.ok) throw new Error('Failed to search venues');
    const data = await res.json();
    return data.data;
  }

  async extractCriteria(
    destinationId: string,
    query: string,
    context: SearchContext
  ): Promise<SearchCriteria> {
    const res = await apiFetch(`/api/destinations/${destinationId}/search/extract-criteria`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        natural_language_query: query,
        context,
      }),
    });
    if (!res.ok) throw new Error('Failed to extract criteria');
    const data = await res.json();
    return data.data.criteria;
  }

  async getLatestSearchResults(destinationId: string): Promise<SearchResult | null> {
    const res = await apiFetch(`/api/destinations/${destinationId}/search/latest`);
    if (!res.ok) throw new Error('Failed to fetch latest search results');
    const data = await res.json();
    return data.data; // null if no results, or SearchResult object
  }

  // ===============================
  // Shortlist Management
  // ===============================

  async addToShortlist(
    destinationId: string,
    input: AddToShortlistInput
  ): Promise<VenueShortlistItem> {
    const res = await apiFetch(`/api/destinations/${destinationId}/shortlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error('Failed to add to shortlist');
    const data = await res.json();
    return data.data.shortlist_item;
  }

  async getShortlist(destinationId: string): Promise<ShortlistWithVenue[]> {
    const res = await apiFetch(`/api/destinations/${destinationId}/shortlist`);
    if (!res.ok) throw new Error('Failed to fetch shortlist');
    const data = await res.json();
    return data.data.items;
  }

  async getAllShortlistedVenuesForEvent(
    eventId: string
  ): Promise<{
    destinations: Array<{
      destination: ResearchDestination;
      shortlist: ShortlistWithVenue[];
    }>;
    total_venues: number;
  }> {
    const res = await apiFetch(`/api/events/${eventId}/shortlist/all`);
    if (!res.ok) throw new Error('Failed to fetch all shortlisted venues');
    const data = await res.json();
    return data.data;
  }

  async updateShortlistItem(
    shortlistId: string,
    input: UpdateShortlistInput
  ): Promise<void> {
    const res = await apiFetch(`/api/shortlist/${shortlistId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error('Failed to update shortlist item');
  }

  async removeFromShortlist(shortlistId: string): Promise<void> {
    const res = await apiFetch(`/api/shortlist/${shortlistId}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const json = await res.json().catch(() => null);
      throw new Error(json?.message || 'Failed to remove from shortlist');
    }
  }

  /**
   * Mark a shortlist item as having a manually submitted proposal
   * (e.g., when venue submits proposal via email instead of Cvent)
   */
  async markManualProposal(
    shortlistId: string,
    data: {
      lowestRoomRate: number | null;
      totalCost: number | null;
      foodBeverageMinimum: number | null;
      currencyCode: string;
      notes: string;
      checkInDate: string | null;
      checkOutDate: string | null;
    }
  ): Promise<void> {
    const res = await apiFetch(`/api/shortlist/${shortlistId}/mark-manual-proposal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to mark proposal as manually submitted');
    }
  }

  /**
   * Mark a shortlist item as manually declined
   * (e.g., when venue declines via email instead of Cvent)
   */
  async markManualDeclined(
    shortlistId: string,
    data: {
      notes?: string;
    }
  ): Promise<void> {
    const res = await apiFetch(`/api/shortlist/${shortlistId}/mark-manual-declined`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to mark proposal as declined');
    }
  }

  /**
   * Unmark a shortlist item as manual proposal (undo manual marking).
   * Clears proposal data so the venue can be reconnected to Cvent.
   */
  async unmarkManualProposal(shortlistId: string): Promise<void> {
    const res = await apiFetch(`/api/shortlist/${shortlistId}/unmark-manual-proposal`, {
      method: 'POST',
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to remove manual proposal marking');
    }
  }

  /**
   * Move a shortlist item to a different destination
   */
  async moveShortlistItem(
    shortlistId: string,
    destinationId: string
  ): Promise<{ shortlistId: string; previousDestinationId: string; newDestinationId: string }> {
    const res = await apiFetch(`/api/shortlist/${shortlistId}/move`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ destinationId }),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to move shortlist item');
    }
    const data = await res.json();
    return data.data;
  }

  async reorderShortlist(destinationId: string, venueIds: string[]): Promise<void> {
    const res = await apiFetch(`/api/destinations/${destinationId}/shortlist/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ venue_ids: venueIds }),
    });
    if (!res.ok) throw new Error('Failed to reorder shortlist');
  }

  async getShortlistItem(shortlistId: string): Promise<VenueShortlistItem> {
    const res = await apiFetch(`/api/research/shortlist/${shortlistId}`);
    if (!res.ok) throw new Error('Failed to fetch shortlist item');
    const data = await res.json();
    return data.data;
  }

  // ===============================
  // Cvent Proposal PDF Management
  // ===============================

  async fetchProposalPdf(shortlistId: string, forceRefresh = false): Promise<{
    attachmentId: string;
    url: string;
    filename: string;
    fetchedAt: string;
  }> {
    const res = await apiFetch(`/api/research/shortlist/${shortlistId}/fetch-proposal-pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ forceRefresh }),
    });
    
    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: 'Failed to fetch proposal PDF' }));
      throw new Error(error.message || 'Failed to fetch proposal PDF');
    }
    
    const result = await res.json();
    return result.data;
  }

  // ===============================
  // Selection Step - Submitted Proposals
  // ===============================

  /**
   * Get all submitted proposals for an event, optionally filtered by approval status
   * Returns data grouped by city for the Selection step
   */
  async initializeProposalActivitySources(eventId:string):Promise<void> {
    const res=await apiFetch(`/api/events/${eventId}/proposal-activity-sources/initialize`,{method:'POST'});
    // Read-only planners can still view migrated lists; only editors initialize new ones.
    if (res.status === 403) return;
    if(!res.ok)throw new Error('Failed to prepare proposal activity lists');
  }
  async selectProposalActivitySource(eventId:string,city:string,country:string,research_destination_id:string):Promise<void> {
    const res=await apiFetch(`/api/events/${eventId}/proposal-activity-source`,{method:'PUT',body:JSON.stringify({city,country,research_destination_id})});
    if(!res.ok)throw new Error('Failed to change the proposal activity list');
  }

  async getProposalDestinationOrder(eventId: string): Promise<{ destination_ids: string[] }> {
    const res = await apiFetch(`/api/events/${eventId}/proposal-destination-order`);
    if (!res.ok) throw new Error('Unable to load destination order. Refresh and try again.');
    return (await res.json()).data;
  }

  async saveProposalDestinationOrder(eventId: string, ids: string[], expected: string[]): Promise<{ destination_ids: string[] }> {
    const res = await apiFetch(`/api/events/${eventId}/proposal-destination-order`, {
      method: 'PUT', body: JSON.stringify({ destination_ids: ids, expected_destination_ids: expected }),
    });
    if (res.status === 409) throw new Error('Someone changed this order. Refresh the proposal before trying again.');
    if (!res.ok) throw new Error('Could not save destination order. Your changes have not been applied.');
    return (await res.json()).data;
  }

  async getSubmittedProposals(
    eventId: string,
    approvalStatus: 'pending' | 'approved' | 'denied' | 'all' = 'all'
  ): Promise<SubmittedProposalsResponse> {
    const res = await apiFetch(
      `/api/events/${eventId}/selection/submitted-proposals?approval_status=${approvalStatus}`
    );
    if (!res.ok) throw new Error('Failed to fetch submitted proposals');
    const data = await res.json();
    return data.data;
  }

  /**
   * Update approval decision for a shortlist item
   */
  async updateApprovalDecision(
    shortlistId: string,
    approval: {
      approval_status: 'approved' | 'denied' | null;
      decision_note?: string | null;
    }
  ): Promise<void> {
    await this.updateShortlistItem(shortlistId, approval);
  }

  // ===============================
  // Venue Details
  // ===============================

  /**
   * Get detailed information about a venue
   */
  async getVenueDetails(venueId: string): Promise<VenueDetails> {
    const res = await apiFetch(`/api/venues/${venueId}`);
    if (!res.ok) throw new Error('Failed to fetch venue details');
    const data = await res.json();
    return data.data;
  }
}
